import { useState, useRef } from "react";
import { Upload, Loader2, X } from "lucide-react";
import { toast } from "sonner";
import { api } from "../../lib/api";
import { resolveImage } from "../../lib/image";

// ---------- Reusable inputs ----------

export const TextField = ({ label, value, onChange, placeholder, multiline, rows = 3 }) => (
  <label className="block">
    {label && <span className="block overline mb-1.5">{label}</span>}
    {multiline ? (
      <textarea
        rows={rows}
        value={value || ""}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
        className="w-full bg-midnight-3 border border-white/10 focus:border-coral px-3 py-2 text-white placeholder:text-white/30 outline-none font-display text-sm transition-colors resize-vertical"
      />
    ) : (
      <input
        type="text"
        value={value || ""}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
        className="w-full bg-midnight-3 border border-white/10 focus:border-coral px-3 py-2 text-white placeholder:text-white/30 outline-none font-display text-sm transition-colors"
      />
    )}
  </label>
);

export const ImageField = ({ label, value, onChange }) => {
  const [uploading, setUploading] = useState(false);
  const inputRef = useRef(null);

  const handleFile = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const res = await api.post("/admin/upload", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      onChange(res.data.url);
      toast.success("Изображение загружено");
    } catch (err) {
      toast.error("Не удалось загрузить");
    } finally {
      setUploading(false);
      if (inputRef.current) inputRef.current.value = "";
    }
  };

  return (
    <div>
      {label && <div className="overline mb-1.5">{label}</div>}
      <div className="flex items-center gap-3">
        <div className="w-16 h-16 bg-midnight-3 border border-white/10 flex items-center justify-center overflow-hidden shrink-0">
          {value ? (
            <img src={resolveImage(value)} alt="" className="w-full h-full object-cover" />
          ) : (
            <span className="text-white/30 text-[10px] font-mono">no img</span>
          )}
        </div>
        <input
          type="text"
          value={value || ""}
          onChange={(e) => onChange(e.target.value)}
          placeholder="URL или /api/files/..."
          className="flex-1 bg-midnight-3 border border-white/10 focus:border-coral px-3 py-2 text-white placeholder:text-white/30 outline-none font-display text-sm transition-colors"
        />
        <input
          type="file"
          ref={inputRef}
          accept="image/*"
          className="hidden"
          onChange={handleFile}
        />
        <button
          type="button"
          onClick={() => inputRef.current?.click()}
          disabled={uploading}
          className="inline-flex items-center gap-2 px-3 py-2 border border-white/15 hover:border-coral hover:text-coral font-mono text-[11px] tracking-widest uppercase transition-colors disabled:opacity-50 shrink-0"
        >
          {uploading ? <Loader2 size={13} className="animate-spin" /> : <Upload size={13} />}
          {uploading ? "..." : "Upload"}
        </button>
      </div>
    </div>
  );
};

export const VideoField = ({ label, value, onChange }) => {
  const [uploading, setUploading] = useState(false);
  const inputRef = useRef(null);

  const handleFile = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const res = await api.post("/admin/upload", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      onChange(res.data.url);
      toast.success("Видео загружено");
    } catch {
      toast.error("Не удалось загрузить видео");
    } finally {
      setUploading(false);
      if (inputRef.current) inputRef.current.value = "";
    }
  };

  return (
    <div>
      {label && <div className="overline mb-1.5">{label}</div>}
      <div className="flex items-center gap-3">
        <input
          type="text"
          value={value || ""}
          onChange={(e) => onChange(e.target.value)}
          placeholder="YouTube ссылка или /api/files/...mp4"
          className="flex-1 bg-midnight-3 border border-white/10 focus:border-coral px-3 py-2 text-white placeholder:text-white/30 outline-none font-display text-sm transition-colors"
        />
        <input
          type="file"
          ref={inputRef}
          accept="video/*"
          className="hidden"
          onChange={handleFile}
        />
        <button
          type="button"
          onClick={() => inputRef.current?.click()}
          disabled={uploading}
          className="inline-flex items-center gap-2 px-3 py-2 border border-white/15 hover:border-coral hover:text-coral font-mono text-[11px] tracking-widest uppercase transition-colors disabled:opacity-50 shrink-0"
        >
          {uploading ? <Loader2 size={13} className="animate-spin" /> : <Upload size={13} />}
          {uploading ? "..." : "Загрузить видео"}
        </button>
      </div>
    </div>
  );
};

export const ListEditor = ({ label, items, fields, onChange, addLabel = "Добавить" }) => {
  const list = items || [];
  const addItem = () => {
    const empty = {};
    fields.forEach((f) => (empty[f.key] = ""));
    onChange([...list, empty]);
  };
  const removeAt = (idx) => onChange(list.filter((_, i) => i !== idx));
  const updateAt = (idx, key, val) => {
    const next = [...list];
    next[idx] = { ...next[idx], [key]: val };
    onChange(next);
  };
  return (
    <div className="space-y-3">
      {label && <div className="overline">{label}</div>}
      {list.map((item, idx) => (
        <div
          key={idx}
          className="bg-midnight-2 border border-white/10 p-4 relative space-y-3"
        >
          <div className="flex justify-between items-center">
            <div className="font-mono text-[11px] text-white/40 tracking-widest uppercase">
              #{idx + 1}
            </div>
            <button
              type="button"
              onClick={() => removeAt(idx)}
              className="text-white/40 hover:text-brand-red"
              title="Удалить"
            >
              <X size={16} />
            </button>
          </div>
          {fields.map((f) =>
            f.type === "image" ? (
              <ImageField
                key={f.key}
                label={f.label}
                value={item[f.key]}
                onChange={(v) => updateAt(idx, f.key, v)}
              />
            ) : (
              <TextField
                key={f.key}
                label={f.label}
                value={item[f.key]}
                onChange={(v) => updateAt(idx, f.key, v)}
                multiline={f.multiline}
                rows={f.rows}
              />
            )
          )}
        </div>
      ))}
      <button
        type="button"
        onClick={addItem}
        className="w-full py-3 border border-dashed border-white/15 hover:border-coral hover:text-coral text-white/60 font-mono text-[11px] tracking-widest uppercase transition-colors"
      >
        + {addLabel}
      </button>
    </div>
  );
};

export const StringListEditor = ({ label, items, onChange, addLabel = "Добавить" }) => {
  const list = items || [];
  const update = (idx, val) => {
    const next = [...list];
    next[idx] = val;
    onChange(next);
  };
  const remove = (idx) => onChange(list.filter((_, i) => i !== idx));
  return (
    <div className="space-y-3">
      {label && <div className="overline">{label}</div>}
      {list.map((v, i) => (
        <div key={i} className="flex items-center gap-2">
          <input
            type="text"
            value={v}
            onChange={(e) => update(i, e.target.value)}
            className="flex-1 bg-midnight-3 border border-white/10 focus:border-coral px-3 py-2 text-white outline-none font-display text-sm transition-colors"
          />
          <button
            type="button"
            onClick={() => remove(i)}
            className="text-white/40 hover:text-brand-red p-2"
          >
            <X size={16} />
          </button>
        </div>
      ))}
      <button
        type="button"
        onClick={() => onChange([...list, ""])}
        className="w-full py-2.5 border border-dashed border-white/15 hover:border-coral hover:text-coral text-white/60 font-mono text-[11px] tracking-widest uppercase transition-colors"
      >
        + {addLabel}
      </button>
    </div>
  );
};

export const Toggle = ({ label, value, onChange }) => (
  <label className="flex items-center justify-between gap-4 cursor-pointer py-3 border-b border-white/5">
    <span className="font-display text-white text-[14px]">{label}</span>
    <button
      type="button"
      role="switch"
      aria-checked={!!value}
      onClick={() => onChange(!value)}
      className={`relative w-11 h-6 transition-colors ${
        value ? "bg-coral" : "bg-white/15"
      }`}
    >
      <span
        className={`absolute top-0.5 w-5 h-5 bg-white transition-transform ${
          value ? "translate-x-[22px]" : "translate-x-0.5"
        }`}
      />
    </button>
  </label>
);

export const ColorField = ({ label, value, onChange }) => (
  <label className="flex items-center gap-4 py-3 border-b border-white/5">
    <input
      type="color"
      value={value || "#000000"}
      onChange={(e) => onChange(e.target.value)}
      className="w-12 h-12 rounded-none cursor-pointer bg-transparent border border-white/10"
    />
    <div className="flex-1">
      <div className="font-display text-white text-[14px]">{label}</div>
      <input
        type="text"
        value={value || ""}
        onChange={(e) => onChange(e.target.value)}
        className="mt-1 w-full bg-midnight-3 border border-white/10 focus:border-coral px-3 py-1.5 text-white outline-none font-mono text-xs transition-colors"
      />
    </div>
  </label>
);

export const SectionHeader = ({ title }) => (
  <h3 className="font-display font-extrabold text-white text-xl mb-5 uppercase tracking-tight">
    {title}
  </h3>
);
